/**
 * Frontend monitoring — PostHog-only error tracking.
 *
 * Public API:
 *   initMonitoring()                  — call once in main.jsx
 *   captureError(error, context)      — report a caught error
 *   captureMessage(msg, level, data)  — report a named event
 *   wrapFetch(input, init)            — fetch() replacement that auto-reports failures
 */

// ── Structured log helpers ─────────────────────────────────────────────────

function structuredLog(level, event, data = {}) {
  const payload = JSON.stringify({ level, event, ...data, ts: Date.now(), src: 'frontend' });
  if (level === 'error') console.error(payload);
  else if (level === 'warn') console.warn(payload);
  else console.log(payload);
}

// ── Public API ─────────────────────────────────────────────────────────────

export async function initMonitoring() {
  // Global unhandled exception handler
  window.addEventListener('error', (event) => {
    captureError(event.error ?? new Error(event.message), 'window.onerror');
  });

  // Global unhandled promise rejection handler.
  // Do NOT call event.preventDefault() — suppressing it hides auth and
  // network errors from the browser console, making failures undiagnosable.
  window.addEventListener('unhandledrejection', (event) => {
    captureError(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      'unhandledrejection',
    );
  });

  // Intercept console.error — only forward actual Error instances to PostHog.
  // Strings are skipped: minified code produces meaningless short strings like
  // "Aa" that create noise without a real stack trace.
  const _origConsoleError = console.error.bind(console);
  console.error = (...args) => {
    _origConsoleError(...args);
    const first = args[0];
    if (first instanceof Error) {
      captureError(first, 'console.error');
    }
  };

  structuredLog('info', 'monitoring.init', {});
}

export function captureError(error, context = '') {
  structuredLog('error', 'frontend.error', {
    message: error?.message ?? String(error),
    context,
    stack: error?.stack?.split('\n').slice(0, 5).join(' | '),
  });
  import('../lib/analytics').then(m => {
    m.captureException(error, context);
  }).catch(() => {});
}

export const captureException = captureError;

export function captureMessage(msg, level = 'info', data = {}) {
  structuredLog(level, `frontend.${msg}`, data);
}

/**
 * Drop-in fetch replacement that reports non-2xx responses and network errors.
 * Usage: import { wrapFetch as fetch } from '@/lib/monitoring';
 */
export async function wrapFetch(input, init) {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  try {
    const res = await fetch(input, init);
    if (!res.ok) {
      structuredLog('warn', 'frontend.fetch.error', {
        url,
        status: res.status,
        method: init?.method ?? 'GET',
      });
      // Threshold: 5+ fetch errors from same user → escalate via captureMessage
      captureMessage('fetch.non2xx', 'warn', { url, status: res.status });
    }
    return res;
  } catch (err) {
    captureError(err, `fetch:${url}`);
    throw err;
  }
}
